import React from 'react';
import cn from 'classnames';
import { Logo } from '@components/Icons';
import { appVersion } from '@app/config';
import { SettingsInlineButton, Section, SettingsSectionBox } from '@views/Settings/components';
import { AppleIcon, DesktopAltIcon, MicrosoftIcon } from '@components/Icons/IntegrationIcons';
import Graphic from '@views/WelcomeGuide/Graphic';
import { useDispatch } from 'react-redux';
import { toggleWelcomeGuideVisibility } from '@actions/ui';
import { useTheme } from '@support/hooks/use-theme';
import claySig from '@app/img/settings/claySig.png';
import signatures from '@app/img/settings/sigs.png';
import styles from './About.module.scss';

const DesktopDownloadLink = () => {
  let url = 'https://clay.earth/start';
  let icon = <DesktopAltIcon />;
  let downloadFor = 'Desktop';

  if (window.app.isElectron && window.desktopIntegrations) {
    url = 'https://clay.earth/api/download/windows';
    icon = <MicrosoftIcon width="14" height="14" />;
    downloadFor = 'Windows';

    if (window.desktopIntegrations.appInfo.os === 'Windows') {
      url = 'https://clay.earth/api/download/mac';
      icon = <AppleIcon width="17" height="17" />;
      downloadFor = 'Mac';
    }
  }

  return (
    <a className={cn(styles.boxLink, styles.download)} href={url} target="_blank">
      {icon}
      Download for {downloadFor}
    </a>
  );
};

export default function AboutSettingsPanel() {
  const dispatch = useDispatch();
  const [theme] = useTheme();

  return (
    <>
      <Section className={styles.versionInfo}>
        <div className={styles.logoBox}>
          <Logo height={20} customColor="#fff" />
        </div>
        <div className={styles.build}>
          Version {appVersion}
          {window.app.isElectron && window.desktopIntegrations && (
            <span>
              ({window.desktopIntegrations.appInfo.os} {window.desktopIntegrations.appInfo.arch}{' '}
              {window.desktopIntegrations.appInfo.version})
            </span>
          )}
        </div>
      </Section>
      <Section>
        <SettingsSectionBox className={styles.welcomeGuideBox}>
          <Graphic className={styles.welcomeGraphic} />
          Welcome Guide
          <p>Your introduction to Clay</p>
          <SettingsInlineButton
            onClick={() => dispatch(toggleWelcomeGuideVisibility(true))}
            className={styles.welcomeGuideBtn}
          >
            Read
          </SettingsInlineButton>
        </SettingsSectionBox>
      </Section>
      <Section className={styles.links}>
        <a className={styles.boxLink} href="https://clay.earth/new" target="_blank">
          What's new
          <p>Updates &amp; improvements</p>
        </a>
        <a className={styles.boxLink} href="https://library.clay.earth/" target="_blank">
          Help &amp; Support
          <p>In-depth guides &amp; FAQ's</p>
        </a>
        <a
          className={cn(styles.boxLink, styles.download)}
          href="https://apps.apple.com/us/app/clay/id1463073824"
          target="_blank"
        >
          <AppleIcon width="17" height="17" />
          Download for iOS
        </a>
        <DesktopDownloadLink />
      </Section>
      <Section>
        <div className={styles.statement}>
          <p>
            We believe in creating work that we're
            <br />
            proud to sign our name on.
          </p>
          <img className={cn(styles.claySig, theme === 'light' && styles.imgLight)} src={claySig} />
          <img
            className={cn(styles.signatures, theme === 'light' && styles.imgLight)}
            src={signatures}
          />
        </div>
      </Section>
      <div className={styles.footer}>
        &copy; Clay Software Inc. &nbsp;·&nbsp;
        <a href="https://clay.earth/privacy" target="_blank">
          Privacy Policy
        </a>
        &nbsp;·&nbsp;
        <a href="https://clay.earth/terms" target="_blank">
          Terms of Use
        </a>
      </div>
    </>
  );
}
